import React from 'react';
import { Stack, Tab, Tabs } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import i18n from '../../i18n';
import { AuxiliaryModeToolbarToggle } from '../../component/auxiliary-mode-toolbar-toggle/auxiliary-mode-toolbar-toggle.component';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import './scss/rbp-cloud-project-setting-toolbar.scss';

/** 云 RBP「子功能管理」路由前缀；各二级 Tab（含首页与营销）均在此前缀下 */
export const SUB_FUNCTION_BASE = '/main/rbp-cloud/sub-function';

/** 「首页与营销设置」与子功能二级路径，与自助点餐等一致挂在 `SUB_FUNCTION_BASE` 下 */
export const SUB_FUNCTION_FIRST_SCREEN_PATH = `${SUB_FUNCTION_BASE}/first-screen`;

/** 顶部 Tab 与设计稿 node 1838:52036 顺序一致：自助点餐 → 首页与营销 → KWA…→ 呼叫服务；`i18nKey` 仅用于 Tab 文案，与各子页内页面主标题（多为 `*_heading`）分离 */
export const RBP_CLOUD_PROJECT_SETTING_TABS: { path: string; i18nKey: string }[] = [
    { path: `${SUB_FUNCTION_BASE}/self-order`, i18nKey: 'self_order_setting_page_label' },
    { path: SUB_FUNCTION_FIRST_SCREEN_PATH, i18nKey: 'first_screen_setting_page_label' },
    { path: `${SUB_FUNCTION_BASE}/kwa`, i18nKey: 'kwa_setting_page_label' },
    { path: `${SUB_FUNCTION_BASE}/rwa`, i18nKey: 'rwa_setting_page_label' },
    { path: `${SUB_FUNCTION_BASE}/mop`, i18nKey: 'mop_setting_page_label' },
    { path: `${SUB_FUNCTION_BASE}/cop`, i18nKey: 'cop_setting_page_label' },
    { path: `${SUB_FUNCTION_BASE}/qop`, i18nKey: 'qop_setting_page_label' },
    { path: `${SUB_FUNCTION_BASE}/dis`, i18nKey: 'dis_setting_page_label' },
    { path: `${SUB_FUNCTION_BASE}/call-service`, i18nKey: 'call_service_setting_page_label' },
];

/**
 * 根据当前 URL 解析应高亮的 Tab（取长路径匹配，避免 /sub-function 误匹配子路径）。
 * @param pathname - 当前 hash 路由 pathname
 * @returns 与 MUI Tabs value 对应的完整路径
 */
export function projectSettingTabValueFromPath(pathname: string): string {
    const candidates = RBP_CLOUD_PROJECT_SETTING_TABS.filter(
        (t) => pathname === t.path || pathname.startsWith(`${t.path}/`),
    );
    if (candidates.length === 0) {
        return RBP_CLOUD_PROJECT_SETTING_TABS[0].path;
    }
    return candidates.sort((a, b) => b.path.length - a.path.length)[0].path;
}

/**
 * 云 RBP 子功能模块共用顶栏：横向 Tab + 右侧「辅助模式」（整块可点切换；样式见公共组件 `AuxiliaryModeToolbarToggle`）。
 */
export function RbpCloudProjectSettingToolbar() {
    const history = useHistory();
    const { pathname } = useLocation();
    const tabValue = projectSettingTabValueFromPath(pathname);
    const { auxiliaryModeEnabled, setAuxiliaryModeEnabled } = React.useContext(RbpCloudSubFunctionAuxiliaryModeContext);

    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
            className="rbp-cloud-project-setting-toolbar"
        >
            <Tabs
                className="rbp-cloud-project-setting-toolbar__tabs"
                value={tabValue}
                onChange={(_, value: string) => history.push(value)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ style: { display: 'none' } }}
                sx={{ flex: 1, minWidth: 0 }}
            >
                {RBP_CLOUD_PROJECT_SETTING_TABS.map((t) => (
                    <Tab
                        key={t.path}
                        value={t.path}
                        disableRipple
                        label={i18n.t(t.i18nKey)}
                        className="rbp-cloud-project-setting-toolbar__tab"
                    />
                ))}
            </Tabs>
            <AuxiliaryModeToolbarToggle
                enabled={auxiliaryModeEnabled}
                onToggle={() => setAuxiliaryModeEnabled(!auxiliaryModeEnabled)}
            />
        </Stack>
    );
}
